import {
  type BatchResponse,
  HttpClient,
  PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";
import { toEpochNanoseconds } from "@/shared/time/time";

import {
  AggregateMetricsRequest,
  CreateMetricDefinitionRequest,
  MetricDefinitionRecord,
  MetricQueryPage,
  MetricRecord,
  NumericAggregateMetricsResponse,
  PublishMetricsRequest,
  QueryMetricsRequest,
  UpdateMetricDefinitionRequest,
} from "./MetricRecord";

interface Options {
  abortSignal: AbortSignal;
  orgId: string;
}

interface ListOptions extends Options {
  pageToken: string;
  pageSize: number;
}

interface QueryOptions extends Options {
  pageToken: string;
}

/**
 * Page size for exhausting the metric-definitions listing. The endpoint defaults
 * to 100 and enforces no maximum, so a large page keeps the number of sequential
 * round-trips low (~15k definitions → 15 requests, not 150).
 */
const METRIC_DEFINITIONS_PAGE_SIZE = 1000;

/**
 * Frontend mirror of the Python `roboto.domain.metrics` SDK.
 *
 * Routes wired to `packages/roboto_service/src/roboto_service/routes/metrics_router.py`:
 *   - `GET    v1/metrics/definitions/`                  → listMetricDefinitions
 *   - `POST   v1/metrics/definition/`                   → createMetricDefinition
 *   - `GET    v1/metrics/definition/{name}`             → getMetricDefinitionByName
 *   - `PUT    v1/metrics/definition/{name}`             → updateMetricDefinition
 *   - `DELETE v1/metrics/definition/{name}`             → deleteMetricDefinition
 *   - `POST   v1/metrics/`                              → publishMetrics (bulk)
 *   - `GET    v1/metrics/session/{sessionId}/`          → listMetricsBySession
 *   - `POST   v1/metrics/query`                         → queryMetrics (paginated)
 *   - `POST   v1/metrics/aggregate`                     → aggregateMetric
 */
export class MetricsService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createMetricDefinition(
    request: CreateMetricDefinitionRequest,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/definition/`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<MetricDefinitionRecord>();
  }

  public async getMetricDefinitionByName(
    name: string,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return response.json<MetricDefinitionRecord>();
  }

  public async updateMetricDefinition(
    name: string,
    request: UpdateMetricDefinitionRequest,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<MetricDefinitionRecord>();
  }

  public async deleteMetricDefinition(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();
  }

  public async listMetricDefinitions(
    options?: Partial<ListOptions>,
  ): Promise<PaginatedResponse<MetricDefinitionRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.pageSize !== undefined) {
      searchParams.append("page_size", String(options.pageSize));
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definitions/`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return response.json<PaginatedResponse<MetricDefinitionRecord>>();
  }

  /**
   * Fetches every page of a definitions listing, returning a flat array.
   * Convenience method for callers that need the full list.
   */
  public async getAllMetricDefinitions(
    options?: Partial<Pick<Options, "abortSignal" | "orgId">>,
  ): Promise<MetricDefinitionRecord[]> {
    const all: MetricDefinitionRecord[] = [];
    let pageToken: string | undefined;
    while (true) {
      const page = await this.listMetricDefinitions({
        abortSignal: options?.abortSignal,
        orgId: options?.orgId,
        pageToken,
        pageSize: METRIC_DEFINITIONS_PAGE_SIZE,
      });
      all.push(...page.items);
      if (!page.next_token) {
        break;
      }
      pageToken = page.next_token;
    }
    return all;
  }

  /**
   * Records a batch of metric values against one session.
   *
   * The server answers 201 once it has processed the request, even when it refused every entry, so
   * this promise resolving is not evidence that anything was recorded. Split the returned batch with
   * `batchSucceeded` / `batchFailed` to see which entries the server kept and why it refused the
   * rest. The promise rejects when the request fails as a whole, for example when the session does
   * not exist, or when `device_id` is omitted for a session without exactly one attached device.
   */
  public async publishMetrics(
    request: PublishMetricsRequest,
    options?: Partial<Options>,
  ): Promise<BatchResponse<MetricRecord>> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<BatchResponse<MetricRecord>>(parseTimestampsAsBigInt);
  }

  public async listMetricsBySession(
    sessionId: string,
    options?: Partial<Options> & { limit?: number },
  ): Promise<MetricRecord[]> {
    const searchParams = new URLSearchParams();
    if (options?.limit !== undefined) {
      searchParams.append("limit", String(options.limit));
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/session/${encodeURIComponent(sessionId)}/`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return response.json<MetricRecord[]>(parseTimestampsAsBigInt);
  }

  public async queryMetrics(
    request: QueryMetricsRequest,
    options?: Partial<QueryOptions>,
  ): Promise<MetricQueryPage> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/query`,
      searchParams,
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      // bigintReplacer keeps the bigint `start_time_ns` / `end_time_ns`
      // full-precision on the wire.
      body: JSON.stringify(request, bigintReplacer),
    });

    return response.json<MetricQueryPage>(parseTimestampsAsBigInt);
  }

  public async aggregateMetric(
    request: AggregateMetricsRequest,
    options?: Partial<Options>,
  ): Promise<NumericAggregateMetricsResponse> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/aggregate`);

    // Nanoseconds are the wire's unit, not the client's. Coercing here is
    // what keeps them out of the domain types and the components.
    const { start_time, end_time, ...rest } = request;
    const body = {
      ...rest,
      start_time_ns: toEpochNanoseconds(start_time),
      end_time_ns: toEpochNanoseconds(end_time),
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      // bigintReplacer keeps the ns bounds full-precision on the wire.
      body: JSON.stringify(body, bigintReplacer),
    });

    return response.json<NumericAggregateMetricsResponse>(
      parseTimestampsAsBigInt,
    );
  }
}
